import { useState } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { Dialog } from '../ui/Dialog/Dialog';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Input } from '../atoms/Input/Input';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { Textarea } from '../atoms/Textarea/Textarea';
import { patchLivestock, type LivestockRecord } from '../../services/livestockService';
import { deceasedDateError, localToday } from './livestockDeceased';
import './MarkDeceasedDialog.css';

export interface MarkDeceasedDialogProps {
  aquariumId: string;
  /** The animal to mark; the dialog is closed while null. */
  record: LivestockRecord | null;
  onClose: () => void;
  /** Called with the saved record after it was marked deceased. */
  onSaved: (record: LivestockRecord) => void;
}

/**
 * "Mark as deceased": date of death (default today, not before the date added, not
 * in the future) and an optional cause/notes. Saved as status 'deceased'; "Undo" on
 * the list sets the animal active again and clears both.
 */
export function MarkDeceasedDialog({ aquariumId, record, onClose, onSaved }: MarkDeceasedDialogProps) {
  const { t } = useTranslation();
  // The parent keys this dialog by the animal, so every animal starts fresh (today, no reason).
  const [today] = useState(localToday);
  const [date, setDate] = useState(today);
  const [reason, setReason] = useState('');
  const [dateError, setDateError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!record) return;
    const problem = deceasedDateError(date, record.dateAdded, today);
    setDateError(problem ? t(problem) : null);
    if (problem) return;
    setSaving(true);
    setSaveError(false);
    try {
      const saved = await patchLivestock(aquariumId, record.id, {
        status: 'deceased',
        deceasedAt: date,
        deceasedReason: reason.trim() || null,
      });
      onSaved(saved);
    } catch {
      // Never the raw backend text.
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      isOpen={record !== null}
      onClose={onClose}
      title={t('livestock.deceased.dialogTitle')}
      size="sm"
      data-testid="mark-deceased-dialog"
    >
      {record && (
        <form onSubmit={submit} noValidate className="mark-deceased">
          <Stack gap="4">
            <Text>{t('livestock.deceased.dialogIntro', { name: record.commonName })}</Text>
            {saveError && <Alert>{t('livestock.deceased.saveError')}</Alert>}
            <Input
              id="livestock-deceased-date"
              type="date"
              label={t('livestock.deceased.date')}
              value={date}
              min={record.dateAdded || undefined}
              max={today}
              onChange={(e) => {
                setDate(e.target.value);
                setDateError(null);
              }}
              error={dateError ?? undefined}
              required
              disabled={saving}
            />
            <Textarea
              id="livestock-deceased-reason"
              label={t('livestock.deceased.reason')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('livestock.deceased.reasonPlaceholder')}
              rows={3}
              maxLength={1000}
              disabled={saving}
            />
            <div className="mark-deceased__actions">
              <Button variant="secondary" type="button" onClick={onClose} disabled={saving}>
                {t('livestock.deceased.cancel')}
              </Button>
              <Button type="submit" isLoading={saving}>
                {t('livestock.deceased.confirm')}
              </Button>
            </div>
          </Stack>
        </form>
      )}
    </Dialog>
  );
}

export default MarkDeceasedDialog;
